import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { logEvent, setUserProperties, hashEmail } from '../utils/analytics';
import { trackCompleteRegistration, trackLogin } from '../utils/metaPixel';
import './LoginPage.css';

const API_BASE_URL = '/api';

// Countries list for autocomplete
const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'Canada', 'Australia',
  'Germany', 'France', 'Italy', 'Spain', 'Netherlands',
  'Switzerland', 'Sweden', 'Norway', 'Denmark', 'Finland',
  'Belgium', 'Austria', 'Ireland', 'New Zealand', 'Singapore',
  'United Arab Emirates', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Oman',
  'Japan', 'South Korea', 'China', 'Malaysia', 'Thailand',
  'Brazil', 'Mexico', 'Argentina', 'Chile', 'Colombia',
  'South Africa', 'Nigeria', 'Kenya', 'Egypt', 'Morocco'
].sort();

function LoginPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('signin');

  // Sign In States
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Sign Up States - Step 1 (All Data)
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userType, setUserType] = useState(''); // 'doctor' or 'medical_student'

  const [fullName, setFullName] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryPracticing, setCountryPracticing] = useState('');
  const [medicalCouncilNumber, setMedicalCouncilNumber] = useState('');
  const [specialityId, setSpecialityId] = useState('');
  const [specialityName, setSpecialityName] = useState('');
  const [licenseFile, setLicenseFile] = useState(null);

  // Student-specific states
  const [countryStudying, setCountryStudying] = useState('');
  const [collegeId, setCollegeId] = useState('');
  const [collegeName, setCollegeName] = useState('');
  const [collegeIdCardFile, setCollegeIdCardFile] = useState(null);

  // Dropdown states
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCountryStudyingDropdown, setShowCountryStudyingDropdown] = useState(false);
  const [showSpecialityDropdown, setShowSpecialityDropdown] = useState(false);
  const [showCollegeDropdown, setShowCollegeDropdown] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState(COUNTRIES);
  const [filteredSpecialities, setFilteredSpecialities] = useState([]);
  const [filteredColleges, setFilteredColleges] = useState([]);
  const [colleges, setColleges] = useState([]);

  // OTP State
  const [otp, setOtp] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');

  // UI States
  const [signupStep, setSignupStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [specialities, setSpecialities] = useState([]);
  const [otpResendCount, setOtpResendCount] = useState(0);

  // Refs for autocomplete
  const specialitiesAutocompleteRef = useRef(null);
  const collegesAutocompleteRef = useRef(null);

  // Fetch specialities on component mount and when user type is doctor
  useEffect(() => {
    if (userType === 'doctor' || userType === '') {
    fetchSpecialities();
    }
  }, [userType]);

  // Fetch colleges when user type is student and country studying is selected
  useEffect(() => {
    if (userType === 'medical_student' && countryStudying) {
      fetchColleges(countryStudying);
    } else {
      setColleges([]);
      setFilteredColleges([]);
    }
  }, [userType, countryStudying]);

  // Update filtered specialities when specialities change
  useEffect(() => {
    setFilteredSpecialities(specialities);
  }, [specialities]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.autocomplete-wrapper')) {
        setShowCountryDropdown(false);
        setShowCountryStudyingDropdown(false);
        setShowSpecialityDropdown(false);
        setShowCollegeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchSpecialities = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/specialities`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        const specialitiesList = data.data || [];
        setSpecialities(specialitiesList);
        setFilteredSpecialities(specialitiesList);
        console.log('Specialities fetched:', specialitiesList.length, specialitiesList);
      } else {
        console.error('Failed to fetch specialities:', data.message);
        setSpecialities([]);
        setFilteredSpecialities([]);
      }
    } catch (err) {
      console.error('Failed to fetch specialities:', err);
      setSpecialities([]);
      setFilteredSpecialities([]);
    }
  };

  const fetchColleges = async (country) => {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/colleges?country=${encodeURIComponent(country)}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        const collegesList = data.data || [];
        setColleges(collegesList);
        setFilteredColleges(collegesList);
        console.log('Colleges fetched:', collegesList.length, collegesList);
      } else {
        console.error('Failed to fetch colleges:', data.message);
        setColleges([]);
        setFilteredColleges([]);
      }
    } catch (err) {
      console.error('Error fetching colleges:', err);
      setColleges([]);
      setFilteredColleges([]);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Track login attempt
    logEvent('Authentication', 'signin_submitted', {
      email_hash: hashEmail(email),
      remember_me: rememberMe,
    });

    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const userType = data.data?.doctor?.user_type || 'unknown';

        // Track successful signin
        logEvent('Authentication', 'signin_success', {
          user_type: userType,
          email_hash: hashEmail(email),
        });

        // Set user properties for segmentation
        setUserProperties({
          user_type: userType,
          user_id: hashEmail(email),
        });

        // Track Meta Pixel Login event
        trackLogin();

        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          // Redirect doctors to AI chat, students to dashboard
          if (data.data && data.data.doctor && data.data.doctor.user_type === 'doctor') {
            navigate('/chat');
          } else {
            navigate('/dashboard');
          }
        }, 1000);
      } else {
        // Track failed signin
        logEvent('Authentication', 'signin_failure', {
          error_reason: data.message || 'Unknown error',
        });
        setError(data.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignupStep1Submit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate user type is selected
    if (!userType) {
      setError('Please select user type');
      return;
    }

    // Validate passwords match
    if (signupPassword !== confirmPassword) {
      setError('Passwords do not match!');
      return;
    }

    // Track signup step 1 completion
    logEvent('Authentication', 'signup_step_1_completed', {
      user_type: userType,
      email_hash: hashEmail(signupEmail),
    });

    // Move to Step 2 (complete profile)
    setSuccess('');
    setSignupStep(2);
  };

  const handleSignupStep2Submit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate based on user type
    if (userType === 'doctor') {
    if (!licenseFile) {
      setError('Please upload your medical license document');
      return;
      }
      if (!specialityId) {
        setError('Please select a specialty');
        return;
      }
      if (!countryPracticing) {
        setError('Please select country practicing in');
        return;
      }
      if (!medicalCouncilNumber) {
        setError('Please enter medical council number');
        return;
      }
    } else if (userType === 'medical_student') {
      if (!collegeIdCardFile) {
        setError('Please upload your college ID card');
        return;
      }
      if (!countryStudying) {
        setError('Please select country studying in');
        return;
      }
      // College is optional - no validation needed
    }

    setLoading(true);

    try {
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('fullName', fullName);
      formData.append('email', signupEmail);
      formData.append('password', signupPassword);
      formData.append('userType', userType);
      formData.append('countryCode', countryCode);
      formData.append('phoneNumber', phoneNumber);

      if (userType === 'doctor') {
      formData.append('countryPracticing', countryPracticing);
      formData.append('medicalCouncilNumber', medicalCouncilNumber);
      formData.append('specialityId', specialityId);
      formData.append('licenseFile', licenseFile);
      } else if (userType === 'medical_student') {
        formData.append('countryStudying', countryStudying);
        formData.append('collegeId', collegeId);
        formData.append('collegeIdCardFile', collegeIdCardFile);
      }

      const response = await fetch(`${API_BASE_URL}/doctor-auth/signup`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Track signup step 2 completion
        logEvent('Authentication', 'signup_step_2_completed', {
          user_type: userType,
          country: userType === 'doctor' ? countryPracticing : countryStudying,
        });

        setMaskedEmail(data.data.email);
        setSuccess('Account created! Please check your email for OTP.');
        setSignupStep(3); // Move to OTP verification (Step 3)
      } else {
        setError(data.message || 'Signup failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('Signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: signupEmail,
          otp: otp
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Track signup completion
        logEvent('Authentication', 'signup_completed', {
          user_type: userType,
          email_hash: hashEmail(signupEmail),
          country: userType === 'doctor' ? countryPracticing : countryStudying,
        });

        // Set user properties
        setUserProperties({
          user_type: userType,
          user_id: hashEmail(signupEmail),
          country: userType === 'doctor' ? countryPracticing : countryStudying,
        });

        // Track Meta Pixel CompleteRegistration event
        trackCompleteRegistration();

        setSuccess('Email verified successfully! Redirecting...');
        setTimeout(() => {
          // Redirect doctors to AI chat, students to dashboard
          if (userType === 'doctor') {
            navigate('/chat');
          } else {
            navigate('/dashboard');
          }
        }, 1500);
      } else {
        setError(data.message || 'OTP verification failed.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('OTP verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async (e) => {
    e.preventDefault();

    if (otpResendCount >= 2) {
      setError('Maximum OTP resend limit reached. Please try again later.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email: signupEmail })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('New OTP sent to your email!');
        setOtpResendCount(otpResendCount + 1);
        setOtp(''); // Clear OTP input
      } else {
        setError(data.message || 'Failed to resend OTP.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Resend OTP error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (e) => {
    const value = e.target.value;
    setCountryPracticing(value);
    setShowCountryDropdown(true);

    // Filter countries
    const filtered = COUNTRIES.filter(country =>
      country.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCountries(filtered);
  };

  const selectCountry = (country) => {
    setCountryPracticing(country);
    setShowCountryDropdown(false);
    setFilteredCountries(COUNTRIES);
  };

  const handleSpecialityChange = (e) => {
    const value = e.target.value;
    setSpecialityName(value);
    setShowSpecialityDropdown(true);
    setSpecialityId('');

    // Filter specialities - show all if empty
    if (value.trim() === '') {
      setFilteredSpecialities(specialities);
    } else {
      const filtered = specialities.filter(spec =>
        spec.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSpecialities(filtered);
    }
  };

  const selectSpeciality = (spec) => {
    setSpecialityName(spec.name);
    setSpecialityId(spec.id);
    setShowSpecialityDropdown(false);
    setFilteredSpecialities(specialities);
  };

  const handleCountryStudyingChange = (e) => {
    const value = e.target.value;
    setCountryStudying(value);
    setShowCountryStudyingDropdown(true);
    // Clear college selection when country changes
    setCollegeId('');
    setCollegeName('');

    // Filter countries
    const filtered = COUNTRIES.filter(country =>
      country.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCountries(filtered);
  };

  const selectCountryStudying = async (country) => {
    setCountryStudying(country);
    setShowCountryStudyingDropdown(false);
    setFilteredCountries(COUNTRIES);
    // Clear college selection when country changes
    setCollegeId('');
    setCollegeName('');
    // Fetch colleges for selected country
    if (country) {
      await fetchColleges(country);
      // Automatically show college dropdown after fetching
      setTimeout(() => {
        setShowCollegeDropdown(true);
      }, 100);
    }
  };

  const handleCollegeChange = (e) => {
    const value = e.target.value;
    setCollegeName(value);
    setShowCollegeDropdown(true);
    setCollegeId('');

    // Filter colleges - show all if empty
    if (value.trim() === '') {
      setFilteredColleges(colleges);
    } else {
      const filtered = colleges.filter(college =>
        college.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredColleges(filtered);
    }
  };

  const selectCollege = (college) => {
    setCollegeName(college.name);
    setCollegeId(college.id);
    setShowCollegeDropdown(false);
    setFilteredColleges(colleges);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        setError('Only PDF, JPG, JPEG, and PNG files are allowed');
        return;
      }
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      if (userType === 'doctor') {
      setLicenseFile(file);
      } else {
        setCollegeIdCardFile(file);
      }
      setError('');
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError('');
    setSuccess('');
    if (tab === 'signup') {
      setSignupStep(1);
      setOtpResendCount(0);
      // Reset all form fields
      setUserType('');
      setFullName('');
      setSignupEmail('');
      setSignupPassword('');
      setConfirmPassword('');
      setCountryCode('+91');
      setPhoneNumber('');
      setCountryPracticing('');
      setMedicalCouncilNumber('');
      setSpecialityId('');
      setSpecialityName('');
      setLicenseFile(null);
      setCountryStudying('');
      setCollegeId('');
      setCollegeName('');
      setCollegeIdCardFile(null);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Left Side - Login Form */}
        <div className="login-form-section">
          <div className="login-header">
            <div className="logo-container" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
              <img src="/logo.svg" alt="S.C.A.L.P.E.L. Logo" className="login-logo" />
            </div>
          </div>

          <h2 className="welcome-text">
            {activeTab === 'signin' ? 'Welcome Back!' : 'Create Your Account'}
          </h2>
          <p className="welcome-subtext">
            {activeTab === 'signin'
              ? 'Log in to your account'
              : signupStep === 1
                ? 'Enter your basic details to get started'
                : signupStep === 2
                  ? 'Complete your profile'
                  : 'Verify your email address'}
          </p>

          <div className="tabs">
            <button
              className={`tab ${activeTab === 'signin' ? 'active' : ''}`}
              onClick={() => handleTabChange('signin')}
            >
              Sign In
            </button>
            <button
              className={`tab ${activeTab === 'signup' ? 'active' : ''}`}
              onClick={() => handleTabChange('signup')}
            >
              Sign Up
            </button>
          </div>

          {/* Error/Success Messages */}
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {/* Sign In Form */}
          {activeTab === 'signin' && (
            <form onSubmit={handleLoginSubmit} className="login-form">
              <div className="input-group">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                  required
                />
              </div>

              <div className="input-group">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input password-input"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M3 3L21 21" stroke="#666" strokeWidth="1.5"/>
                      <path d="M10.5 10.677C10.1888 11.0125 10 11.4792 10 12C10 13.1046 10.8954 14 12 14C12.5208 14 12.9875 13.8112 13.323 13.5" stroke="#666" strokeWidth="1.5"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M3 13C6.6 5 17.4 5 21 13" stroke="#666" strokeWidth="1.5"/>
                      <circle cx="12" cy="13" r="2" stroke="#666" strokeWidth="1.5"/>
                    </svg>
                  )}
                </button>
              </div>

              <div className="form-footer">
                <label className="remember-me">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>
                <a href="#" className="forgot-password">Forgot Password?</a>
              </div>

              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          )}

          {/* Sign Up - Step 1: Basic Info (Name, Email, Password) */}
          {activeTab === 'signup' && signupStep === 1 && (
            <form onSubmit={handleSignupStep1Submit} className="login-form">
              <div className="input-group">
                <input
                  type="text"
                  placeholder="Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="form-input"
                  required
                />
              </div>

              <div className="input-group">
                <input
                  type="email"
                  placeholder="Email Address"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  className="form-input"
                  required
                />
              </div>

              <div className="input-group">
                <input
                  type={showSignupPassword ? "text" : "password"}
                  placeholder="Create Password"
                  value={signupPassword}
                  onChange={(e) => setSignupPassword(e.target.value)}
                  className="form-input password-input"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowSignupPassword(!showSignupPassword)}
                >
                  {showSignupPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M3 3L21 21" stroke="#666" strokeWidth="1.5"/>
                      <path d="M10.5 10.677C10.1888 11.0125 10 11.4792 10 12C10 13.1046 10.8954 14 12 14C12.5208 14 12.9875 13.8112 13.323 13.5" stroke="#666" strokeWidth="1.5"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M3 13C6.6 5 17.4 5 21 13" stroke="#666" strokeWidth="1.5"/>
                      <circle cx="12" cy="13" r="2" stroke="#666" strokeWidth="1.5"/>
                    </svg>
                  )}
                </button>
              </div>

              <div className="input-group">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="form-input password-input"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M3 3L21 21" stroke="#666" strokeWidth="1.5"/>
                      <path d="M10.5 10.677C10.1888 11.0125 10 11.4792 10 12C10 13.1046 10.8954 14 12 14C12.5208 14 12.9875 13.8112 13.323 13.5" stroke="#666" strokeWidth="1.5"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M3 13C6.6 5 17.4 5 21 13" stroke="#666" strokeWidth="1.5"/>
                      <circle cx="12" cy="13" r="2" stroke="#666" strokeWidth="1.5"/>
                    </svg>
                  )}
                </button>
              </div>

              <div className="input-group">
                <select
                  value={userType}
                  onChange={(e) => {
                    const selectedType = e.target.value;
                    setUserType(selectedType);
                    if (selectedType) {
                      logEvent('Authentication', 'signup_started', {
                        user_type: selectedType,
                      });
                    }
                  }}
                  className="form-input"
                  required
                >
                  <option value="">User Type</option>
                  <option value="doctor">Doctor</option>
                  <option value="medical_student">Medical Student</option>
                </select>
              </div>

              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? 'Processing...' : 'Continue'}
              </button>
            </form>
          )}

          {/* Sign Up - Step 2: Complete Profile */}
          {activeTab === 'signup' && signupStep === 2 && (
            <form onSubmit={handleSignupStep2Submit} className="login-form signup-form">
              <div className="input-group-row">
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="form-input country-code-select"
                  required
                >
                  <option value="+91">+91 (India)</option>
                  <option value="+93">+93 (Afghanistan)</option>
                  <option value="+355">+355 (Albania)</option>
                  <option value="+213">+213 (Algeria)</option>
                  <option value="+376">+376 (Andorra)</option>
                  <option value="+244">+244 (Angola)</option>
                  <option value="+54">+54 (Argentina)</option>
                  <option value="+374">+374 (Armenia)</option>
                  <option value="+61">+61 (Australia)</option>
                  <option value="+43">+43 (Austria)</option>
                  <option value="+994">+994 (Azerbaijan)</option>
                  <option value="+973">+973 (Bahrain)</option>
                  <option value="+880">+880 (Bangladesh)</option>
                  <option value="+375">+375 (Belarus)</option>
                  <option value="+32">+32 (Belgium)</option>
                  <option value="+501">+501 (Belize)</option>
                  <option value="+229">+229 (Benin)</option>
                  <option value="+975">+975 (Bhutan)</option>
                  <option value="+591">+591 (Bolivia)</option>
                  <option value="+387">+387 (Bosnia)</option>
                  <option value="+267">+267 (Botswana)</option>
                  <option value="+55">+55 (Brazil)</option>
                  <option value="+673">+673 (Brunei)</option>
                  <option value="+359">+359 (Bulgaria)</option>
                  <option value="+226">+226 (Burkina Faso)</option>
                  <option value="+257">+257 (Burundi)</option>
                  <option value="+855">+855 (Cambodia)</option>
                  <option value="+237">+237 (Cameroon)</option>
                  <option value="+1">+1 (Canada)</option>
                  <option value="+238">+238 (Cape Verde)</option>
                  <option value="+236">+236 (Central African Republic)</option>
                  <option value="+235">+235 (Chad)</option>
                  <option value="+56">+56 (Chile)</option>
                  <option value="+86">+86 (China)</option>
                  <option value="+57">+57 (Colombia)</option>
                  <option value="+269">+269 (Comoros)</option>
                  <option value="+242">+242 (Congo)</option>
                  <option value="+506">+506 (Costa Rica)</option>
                  <option value="+385">+385 (Croatia)</option>
                  <option value="+53">+53 (Cuba)</option>
                  <option value="+357">+357 (Cyprus)</option>
                  <option value="+420">+420 (Czech Republic)</option>
                  <option value="+45">+45 (Denmark)</option>
                  <option value="+253">+253 (Djibouti)</option>
                  <option value="+593">+593 (Ecuador)</option>
                  <option value="+20">+20 (Egypt)</option>
                  <option value="+503">+503 (El Salvador)</option>
                  <option value="+240">+240 (Equatorial Guinea)</option>
                  <option value="+291">+291 (Eritrea)</option>
                  <option value="+372">+372 (Estonia)</option>
                  <option value="+251">+251 (Ethiopia)</option>
                  <option value="+679">+679 (Fiji)</option>
                  <option value="+358">+358 (Finland)</option>
                  <option value="+33">+33 (France)</option>
                  <option value="+241">+241 (Gabon)</option>
                  <option value="+220">+220 (Gambia)</option>
                  <option value="+995">+995 (Georgia)</option>
                  <option value="+49">+49 (Germany)</option>
                  <option value="+233">+233 (Ghana)</option>
                  <option value="+30">+30 (Greece)</option>
                  <option value="+502">+502 (Guatemala)</option>
                  <option value="+224">+224 (Guinea)</option>
                  <option value="+245">+245 (Guinea-Bissau)</option>
                  <option value="+592">+592 (Guyana)</option>
                  <option value="+509">+509 (Haiti)</option>
                  <option value="+504">+504 (Honduras)</option>
                  <option value="+852">+852 (Hong Kong)</option>
                  <option value="+36">+36 (Hungary)</option>
                  <option value="+354">+354 (Iceland)</option>
                  <option value="+62">+62 (Indonesia)</option>
                  <option value="+98">+98 (Iran)</option>
                  <option value="+964">+964 (Iraq)</option>
                  <option value="+353">+353 (Ireland)</option>
                  <option value="+972">+972 (Israel)</option>
                  <option value="+39">+39 (Italy)</option>
                  <option value="+225">+225 (Ivory Coast)</option>
                  <option value="+81">+81 (Japan)</option>
                  <option value="+962">+962 (Jordan)</option>
                  <option value="+7">+7 (Kazakhstan)</option>
                  <option value="+254">+254 (Kenya)</option>
                  <option value="+965">+965 (Kuwait)</option>
                  <option value="+996">+996 (Kyrgyzstan)</option>
                  <option value="+856">+856 (Laos)</option>
                  <option value="+371">+371 (Latvia)</option>
                  <option value="+961">+961 (Lebanon)</option>
                  <option value="+266">+266 (Lesotho)</option>
                  <option value="+231">+231 (Liberia)</option>
                  <option value="+218">+218 (Libya)</option>
                  <option value="+423">+423 (Liechtenstein)</option>
                  <option value="+370">+370 (Lithuania)</option>
                  <option value="+352">+352 (Luxembourg)</option>
                  <option value="+853">+853 (Macau)</option>
                  <option value="+389">+389 (Macedonia)</option>
                  <option value="+261">+261 (Madagascar)</option>
                  <option value="+265">+265 (Malawi)</option>
                  <option value="+60">+60 (Malaysia)</option>
                  <option value="+960">+960 (Maldives)</option>
                  <option value="+223">+223 (Mali)</option>
                  <option value="+356">+356 (Malta)</option>
                  <option value="+222">+222 (Mauritania)</option>
                  <option value="+230">+230 (Mauritius)</option>
                  <option value="+52">+52 (Mexico)</option>
                  <option value="+373">+373 (Moldova)</option>
                  <option value="+377">+377 (Monaco)</option>
                  <option value="+976">+976 (Mongolia)</option>
                  <option value="+382">+382 (Montenegro)</option>
                  <option value="+212">+212 (Morocco)</option>
                  <option value="+258">+258 (Mozambique)</option>
                  <option value="+95">+95 (Myanmar)</option>
                  <option value="+264">+264 (Namibia)</option>
                  <option value="+977">+977 (Nepal)</option>
                  <option value="+31">+31 (Netherlands)</option>
                  <option value="+64">+64 (New Zealand)</option>
                  <option value="+505">+505 (Nicaragua)</option>
                  <option value="+227">+227 (Niger)</option>
                  <option value="+234">+234 (Nigeria)</option>
                  <option value="+850">+850 (North Korea)</option>
                  <option value="+47">+47 (Norway)</option>
                  <option value="+968">+968 (Oman)</option>
                  <option value="+92">+92 (Pakistan)</option>
                  <option value="+970">+970 (Palestine)</option>
                  <option value="+507">+507 (Panama)</option>
                  <option value="+675">+675 (Papua New Guinea)</option>
                  <option value="+595">+595 (Paraguay)</option>
                  <option value="+51">+51 (Peru)</option>
                  <option value="+63">+63 (Philippines)</option>
                  <option value="+48">+48 (Poland)</option>
                  <option value="+351">+351 (Portugal)</option>
                  <option value="+974">+974 (Qatar)</option>
                  <option value="+40">+40 (Romania)</option>
                  <option value="+7">+7 (Russia)</option>
                  <option value="+250">+250 (Rwanda)</option>
                  <option value="+966">+966 (Saudi Arabia)</option>
                  <option value="+221">+221 (Senegal)</option>
                  <option value="+381">+381 (Serbia)</option>
                  <option value="+248">+248 (Seychelles)</option>
                  <option value="+232">+232 (Sierra Leone)</option>
                  <option value="+65">+65 (Singapore)</option>
                  <option value="+421">+421 (Slovakia)</option>
                  <option value="+386">+386 (Slovenia)</option>
                  <option value="+677">+677 (Solomon Islands)</option>
                  <option value="+252">+252 (Somalia)</option>
                  <option value="+27">+27 (South Africa)</option>
                  <option value="+82">+82 (South Korea)</option>
                  <option value="+211">+211 (South Sudan)</option>
                  <option value="+34">+34 (Spain)</option>
                  <option value="+94">+94 (Sri Lanka)</option>
                  <option value="+249">+249 (Sudan)</option>
                  <option value="+597">+597 (Suriname)</option>
                  <option value="+268">+268 (Swaziland)</option>
                  <option value="+46">+46 (Sweden)</option>
                  <option value="+41">+41 (Switzerland)</option>
                  <option value="+963">+963 (Syria)</option>
                  <option value="+886">+886 (Taiwan)</option>
                  <option value="+992">+992 (Tajikistan)</option>
                  <option value="+255">+255 (Tanzania)</option>
                  <option value="+66">+66 (Thailand)</option>
                  <option value="+670">+670 (Timor-Leste)</option>
                  <option value="+228">+228 (Togo)</option>
                  <option value="+676">+676 (Tonga)</option>
                  <option value="+216">+216 (Tunisia)</option>
                  <option value="+90">+90 (Turkey)</option>
                  <option value="+993">+993 (Turkmenistan)</option>
                  <option value="+256">+256 (Uganda)</option>
                  <option value="+380">+380 (Ukraine)</option>
                  <option value="+971">+971 (UAE)</option>
                  <option value="+44">+44 (United Kingdom)</option>
                  <option value="+1">+1 (United States)</option>
                  <option value="+598">+598 (Uruguay)</option>
                  <option value="+998">+998 (Uzbekistan)</option>
                  <option value="+678">+678 (Vanuatu)</option>
                  <option value="+58">+58 (Venezuela)</option>
                  <option value="+84">+84 (Vietnam)</option>
                  <option value="+967">+967 (Yemen)</option>
                  <option value="+260">+260 (Zambia)</option>
                  <option value="+263">+263 (Zimbabwe)</option>
                </select>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="form-input phone-input"
                  required
                />
              </div>

              {/* Doctor-specific fields */}
              {userType === 'doctor' && (
                <>
              <div className="input-group autocomplete-wrapper">
                <input
                  type="text"
                  placeholder="Country Practicing In"
                  value={countryPracticing}
                  onChange={handleCountryChange}
                  onFocus={() => setShowCountryDropdown(true)}
                  className="form-input"
                  required
                  autoComplete="off"
                />
                {showCountryDropdown && filteredCountries.length > 0 && (
                  <div className="custom-dropdown">
                    {filteredCountries.map(country => (
                      <div
                        key={country}
                        className="dropdown-item"
                        onClick={() => selectCountry(country)}
                      >
                        {country}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="input-group">
                <input
                  type="text"
                  placeholder="Medical Council Number"
                  value={medicalCouncilNumber}
                  onChange={(e) => setMedicalCouncilNumber(e.target.value)}
                  className="form-input"
                  required
                />
              </div>

              <div className="input-group autocomplete-wrapper" ref={specialitiesAutocompleteRef}>
                <input
                  type="text"
                  placeholder="Select Specialty"
                  value={specialityName}
                  onChange={handleSpecialityChange}
                  onFocus={() => {
                        // Ensure specialities are loaded
                        if (specialities.length === 0) {
                          fetchSpecialities().then(() => {
                            setShowSpecialityDropdown(true);
                            setFilteredSpecialities(specialities);
                          });
                        } else {
                    setShowSpecialityDropdown(true);
                    if (specialityName.trim() === '') {
                      setFilteredSpecialities(specialities);
                          }
                    }
                  }}
                  className="form-input"
                  required
                  autoComplete="off"
                />
                {showSpecialityDropdown && filteredSpecialities.length > 0 && (
                  <div className="custom-dropdown">
                    {filteredSpecialities.map(spec => (
                      <div
                        key={spec.id}
                        className="dropdown-item"
                        onClick={() => selectSpeciality(spec)}
                      >
                        {spec.name}
                      </div>
                    ))}
                  </div>
                )}
                    {showSpecialityDropdown && filteredSpecialities.length === 0 && specialities.length === 0 && (
                      <div className="custom-dropdown">
                        <div className="dropdown-item" style={{ color: '#999', cursor: 'default' }}>
                          Loading specialities...
                        </div>
                      </div>
                    )}
              </div>

              <div className="input-group file-upload-group">
                <label className="file-upload-label">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="file-input"
                    accept=".pdf,.jpg,.jpeg,.png"
                    required
                  />
                  <span className="file-upload-text">
                    {licenseFile ? licenseFile.name : 'Upload Medical License (PDF/JPG/PNG)'}
                  </span>
                  <svg className="upload-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="#0081A7" strokeWidth="2"/>
                    <path d="M17 8L12 3L7 8M12 3V15" stroke="#0081A7" strokeWidth="2"/>
                  </svg>
                </label>
              </div>
                </>
              )}

              {/* Medical Student-specific fields */}
              {userType === 'medical_student' && (
                <>
                  <div className="input-group autocomplete-wrapper">
                    <input
                      type="text"
                      placeholder="Country Studying In"
                      value={countryStudying}
                      onChange={handleCountryStudyingChange}
                      onFocus={() => setShowCountryStudyingDropdown(true)}
                      className="form-input"
                      required
                      autoComplete="off"
                    />
                    {showCountryStudyingDropdown && filteredCountries.length > 0 && (
                      <div className="custom-dropdown">
                        {filteredCountries.map(country => (
                          <div
                            key={country}
                            className="dropdown-item"
                            onClick={() => selectCountryStudying(country)}
                          >
                            {country}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="input-group autocomplete-wrapper" ref={collegesAutocompleteRef}>
                    <input
                      type="text"
                      placeholder="Select College (Optional)"
                      value={collegeName}
                      onChange={handleCollegeChange}
                      onFocus={() => {
                        if (!countryStudying) {
                          setError('Please select country studying in first');
                          return;
                        }
                        // Ensure colleges are loaded
                        if (colleges.length === 0 && countryStudying) {
                          fetchColleges(countryStudying).then(() => {
                            setShowCollegeDropdown(true);
                          });
                        } else {
                          setShowCollegeDropdown(true);
                          if (collegeName.trim() === '') {
                            setFilteredColleges(colleges);
                          }
                        }
                      }}
                      className="form-input"
                      autoComplete="off"
                      disabled={!countryStudying}
                    />
                    {showCollegeDropdown && filteredColleges.length > 0 && (
                      <div className="custom-dropdown">
                        {filteredColleges.map(college => (
                          <div
                            key={college.id}
                            className="dropdown-item"
                            onClick={() => selectCollege(college)}
                          >
                            {college.name}
                          </div>
                        ))}
                      </div>
                    )}
                    {showCollegeDropdown && filteredColleges.length === 0 && colleges.length === 0 && countryStudying && (
                      <div className="custom-dropdown">
                        <div className="dropdown-item" style={{ color: '#999', cursor: 'default' }}>
                          No colleges found for this country
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="input-group file-upload-group">
                    <label className="file-upload-label">
                      <input
                        type="file"
                        onChange={handleFileChange}
                        className="file-input"
                        accept=".pdf,.jpg,.jpeg,.png"
                        required
                      />
                      <span className="file-upload-text">
                        {collegeIdCardFile ? collegeIdCardFile.name : 'Upload College ID Card (PDF/JPG/PNG)'}
                      </span>
                      <svg className="upload-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="#0081A7" strokeWidth="2"/>
                        <path d="M17 8L12 3L7 8M12 3V15" stroke="#0081A7" strokeWidth="2"/>
                      </svg>
                    </label>
                  </div>
                </>
              )}

              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? 'Creating Account...' : 'Complete Registration'}
              </button>
            </form>
          )}

          {/* Sign Up - Step 3: OTP Verification */}
          {activeTab === 'signup' && signupStep === 3 && (
            <form onSubmit={handleOTPSubmit} className="login-form">
              <div className="otp-info">
                <p className="otp-message">We've sent a verification code to</p>
                <p className="otp-email">{maskedEmail}</p>
              </div>

              <div className="input-group">
                <input
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  className="form-input otp-input"
                  maxLength="6"
                  required
                />
              </div>

              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>

              <div className="resend-otp">
                <span>Didn't receive the code? </span>
                <a
                  href="#"
                  className="resend-link"
                  onClick={handleResendOTP}
                  style={{ pointerEvents: otpResendCount >= 2 ? 'none' : 'auto', opacity: otpResendCount >= 2 ? 0.5 : 1 }}
                >
                  Resend OTP {otpResendCount > 0 && `(${2 - otpResendCount} left)`}
                </a>
              </div>
            </form>
          )}
        </div>

        {/* Right Side - Background Video */}
        <div className="gradient-section">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="login-bg-video"
            src={`${process.env.PUBLIC_URL}/login-bg-video.mp4`}
          />
          <div className="gradient-bg"></div>
        </div>
      </div>

      {/* Product Badge - Bottom Right */}
      <div className="product-badge-bottom">
        <p className="product-badge-text">A product of:</p>
        <a href="https://docplix.com" target="_blank" rel="noopener noreferrer">
          <img src={process.env.PUBLIC_URL + '/docplix.png'} alt="Docplix" className="product-badge-logo" />
        </a>
      </div>
    </div>
  );
}

export default LoginPage;
